import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  Snackbar,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import DataTable, { Column } from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  getSpamPhoneNumbers,
  getSpamPhoneReports,
  createSpamPhoneNumber,
  approveAllSpamReports,
  deleteSpamPhoneNumber,
  updateSpamPhoneLabel,
  SpamPhoneNumber,
  SpamPhoneReport,
  SpamPhoneFormData,
} from '../../services/api/securityApi';
import { formatDateTime } from '../../utils/formatters';

const reportTypeLabels: Record<string, string> = {
  police: '경찰 사칭',
  prosecutor: '검찰 사칭',
  bank: '금융기관 사칭',
  government: '정부기관 사칭',
  fraud: '사기',
  spam: '스팸',
  advertisement: '광고',
  other: '기타',
};

const SpamPhonePage: React.FC = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ page: 0, limit: 20, search: '' });
  const [searchInput, setSearchInput] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; phoneNumber: string | null }>({
    open: false,
    phoneNumber: null,
  });
  const [labelDialog, setLabelDialog] = useState<{ open: boolean; phoneNumber: string; currentLabel: string }>({
    open: false,
    phoneNumber: '',
    currentLabel: '',
  });
  const [labelInput, setLabelInput] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { control, handleSubmit, reset, formState: { errors } } = useForm<SpamPhoneFormData>({
    defaultValues: { phoneNumber: '', reportType: 'spam', description: '', displayLabel: '' },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['spam-phones', filters],
    queryFn: () => getSpamPhoneNumbers(filters),
  });

  const { data: reportDetails, isLoading: detailLoading } = useQuery({
    queryKey: ['spam-phone-detail', selectedPhone],
    queryFn: () => getSpamPhoneReports(selectedPhone!),
    enabled: !!selectedPhone && detailDialogOpen,
  });

  const createMutation = useMutation({
    mutationFn: createSpamPhoneNumber,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spam-phones'] });
      setSnackbar({ open: true, message: '스팸 번호가 등록되었습니다.', severity: 'success' });
      setAddDialogOpen(false);
      reset();
    },
    onError: (e: any) => setSnackbar({ open: true, message: e.message, severity: 'error' }),
  });

  const approveMutation = useMutation({
    mutationFn: ({ phoneNumber }: { phoneNumber: string }) => approveAllSpamReports(phoneNumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spam-phones'] });
      queryClient.invalidateQueries({ queryKey: ['spam-phone-detail', selectedPhone] });
      setSnackbar({ open: true, message: '모든 신고가 승인되었습니다.', severity: 'success' });
    },
    onError: (e: any) => setSnackbar({ open: true, message: e.message, severity: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSpamPhoneNumber,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spam-phones'] });
      setSnackbar({ open: true, message: '스팸 번호가 삭제되었습니다.', severity: 'success' });
      setConfirmDialog({ open: false, phoneNumber: null });
    },
    onError: (e: any) => setSnackbar({ open: true, message: e.message, severity: 'error' }),
  });

  const labelMutation = useMutation({
    mutationFn: ({ phoneNumber, displayLabel }: { phoneNumber: string; displayLabel: string }) =>
      updateSpamPhoneLabel(phoneNumber, displayLabel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spam-phones'] });
      setSnackbar({ open: true, message: '표시 텍스트가 수정되었습니다.', severity: 'success' });
      setLabelDialog({ open: false, phoneNumber: '', currentLabel: '' });
    },
    onError: (e: any) => setSnackbar({ open: true, message: e.message, severity: 'error' }),
  });

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, search: searchInput, page: 0 }));
  };

  const handleOpenDetail = (phoneNumber: string) => {
    setSelectedPhone(phoneNumber);
    setDetailDialogOpen(true);
  };

  const columns: Column<SpamPhoneNumber>[] = [
    {
      id: 'phoneNumber',
      label: '전화번호',
      minWidth: 150,
    },
    {
      id: 'reportCount',
      label: '총 신고 수',
      minWidth: 100,
      align: 'center',
      format: (value: number) => (
        <Chip label={`${value}건`} size="small" color="default" />
      ),
    },
    {
      id: 'approvedCount',
      label: '승인됨',
      minWidth: 100,
      align: 'center',
      format: (value: number) => (
        <Chip label={`${value}건`} size="small" color={value > 0 ? 'error' : 'default'} />
      ),
    },
    {
      id: 'pendingCount',
      label: '미승인',
      minWidth: 100,
      align: 'center',
      format: (value: number) => (
        <Chip label={`${value}건`} size="small" color={value > 0 ? 'warning' : 'default'} />
      ),
    },
    {
      id: 'lastReportedAt',
      label: '최근 신고일',
      minWidth: 160,
      format: (value: string) => formatDateTime(value),
    },
    {
      id: 'displayLabel',
      label: '오버레이 표시 텍스트',
      minWidth: 160,
      format: (value: string | null, row: SpamPhoneNumber) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" color={value ? 'text.primary' : 'text.disabled'}>
            {value || '미설정'}
          </Typography>
          <Tooltip title="표시 텍스트 수정">
            <IconButton
              size="small"
              onClick={() => {
                setLabelInput(value || '');
                setLabelDialog({ open: true, phoneNumber: row.phoneNumber, currentLabel: value || '' });
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      id: 'actions',
      label: '작업',
      minWidth: 160,
      align: 'center',
      format: (_: any, row: SpamPhoneNumber) => (
        <Stack direction="row" spacing={0.5} justifyContent="center">
          <Tooltip title="신고 내역 보기">
            <IconButton size="small" onClick={() => handleOpenDetail(row.phoneNumber)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.pendingCount > 0 && (
            <Tooltip title="미승인 신고 일괄 승인">
              <IconButton
                size="small"
                color="success"
                onClick={() => approveMutation.mutate({ phoneNumber: row.phoneNumber })}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="모든 신고 삭제">
            <IconButton
              size="small"
              color="error"
              onClick={() => setConfirmDialog({ open: true, phoneNumber: row.phoneNumber })}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">스팸 번호 관리</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)}>
          스팸 번호 등록
        </Button>
      </Box>

      {/* 검색 */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          placeholder="전화번호 검색"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleSearch}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ width: 280 }}
        />
      </Box>

      <DataTable
        columns={columns}
        data={data?.items || []}
        total={data?.total || 0}
        page={filters.page}
        rowsPerPage={filters.limit}
        onPageChange={page => setFilters(prev => ({ ...prev, page }))}
        onRowsPerPageChange={limit => setFilters(prev => ({ ...prev, limit, page: 0 }))}
        loading={isLoading}
      />

      {/* 스팸 번호 등록 다이얼로그 */}
      <Dialog open={addDialogOpen} onClose={() => { setAddDialogOpen(false); reset(); }} maxWidth="sm" fullWidth>
        <DialogTitle>스팸 번호 등록</DialogTitle>
        <form onSubmit={handleSubmit(data => createMutation.mutate(data))}>
          <DialogContent>
            <Stack spacing={2}>
              <Controller
                name="phoneNumber"
                control={control}
                rules={{ required: '전화번호를 입력해주세요' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="전화번호"
                    fullWidth
                    placeholder="01012345678"
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message}
                  />
                )}
              />
              <Controller
                name="reportType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>신고 유형</InputLabel>
                    <Select {...field} label="신고 유형">
                      {Object.entries(reportTypeLabels).map(([value, label]) => (
                        <MenuItem key={value} value={value}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
              <Controller
                name="displayLabel"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="표시 라벨 (선택)" fullWidth placeholder="예: 보이스피싱" />
                )}
              />
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="설명 (선택)" fullWidth multiline rows={3} />
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setAddDialogOpen(false); reset(); }}>취소</Button>
            <Button type="submit" variant="contained">등록</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* 신고 내역 상세 다이얼로그 */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>신고 내역 - {selectedPhone}</DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Typography>로딩 중...</Typography>
          ) : (reportDetails || []).length === 0 ? (
            <Typography color="text.secondary">신고 내역이 없습니다.</Typography>
          ) : (
            <Stack spacing={1} sx={{ mt: 1 }}>
              {(reportDetails || []).map((report: SpamPhoneReport) => (
                <Box
                  key={report.id}
                  sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Chip
                      label={reportTypeLabels[report.reportType] || report.reportType}
                      size="small"
                      color="default"
                    />
                    <Chip
                      label={report.status === 'approved' ? '승인' : report.status === 'rejected' ? '거부' : '대기'}
                      size="small"
                      color={report.status === 'approved' ? 'error' : report.status === 'rejected' ? 'default' : 'warning'}
                    />
                    {report.displayLabel && (
                      <Chip label={report.displayLabel} size="small" color="info" />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                      {formatDateTime(report.createdAt)}
                    </Typography>
                  </Stack>
                  {report.description && (
                    <Typography variant="body2" color="text.secondary">{report.description}</Typography>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 라벨 편집 다이얼로그 */}
      <Dialog open={labelDialog.open} onClose={() => setLabelDialog({ open: false, phoneNumber: '', currentLabel: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>오버레이 표시 텍스트 수정</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {labelDialog.phoneNumber} 번호에 전화가 올 때 오버레이에 표시될 텍스트입니다.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="표시 텍스트"
            placeholder="예: 보이스피싱, 대출 사기"
            value={labelInput}
            onChange={e => setLabelInput(e.target.value)}
            helperText="비워두면 '스팸 의심'으로 표시됩니다."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLabelDialog({ open: false, phoneNumber: '', currentLabel: '' })}>취소</Button>
          <Button
            variant="contained"
            onClick={() => labelMutation.mutate({ phoneNumber: labelDialog.phoneNumber, displayLabel: labelInput })}
            disabled={labelMutation.isPending}
          >
            저장
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 */}
      <ConfirmDialog
        open={confirmDialog.open}
        title="스팸 번호 삭제"
        message={`${confirmDialog.phoneNumber} 번호의 모든 신고 내역을 삭제하시겠습니까?`}
        onConfirm={() => confirmDialog.phoneNumber && deleteMutation.mutate(confirmDialog.phoneNumber)}
        onCancel={() => setConfirmDialog({ open: false, phoneNumber: null })}
        confirmText="삭제"
        confirmColor="error"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SpamPhonePage;
